import React, { useContext, useState } from 'react';
import Table from 'react-bootstrap/Table';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteSupplier,
  getSupplierDetailsLazy,
} from '../../clients/supplier_client';
import SupplierModal from './SupplierModal';
import SupplierDetailModal from './SupplierDetailModal';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { logError, logInfiniteScrollDebug } from '../../config_and_helpers/logger';
import SortableHeaderCell from '../Generic/SortableHeaderCell';

/**
 * SupplierTable is a functional component that renders a table of suppliers.
 *
 * @param {Object} supplierList - The list of suppliers to display in the table.
 * @param {Function} handleChange - The function to handle edits made to a supplier.
 * @param {Function} clearSearchValue - The function to clear the search value.
 * @returns {JSX.Element} - Returns the rendered table component.
 */
const SupplierTable = ({
  supplierList,
  handleChange,
  clearSearchValue,
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const SUPPLIERS_TABLE_CONTEXT = 'supplierTable';

  /**
   * Writes supplier-table trace events to the shared in-memory log buffer (and dev console via logger).
   * @param {string} label - Event name for the trace.
   * @param {Object} [data] - Structured metadata for the event.
   */
  const logSupplierTableDebug = (label, data = {}) => {
    const payload = data || {};
    logInfiniteScrollDebug(SUPPLIERS_TABLE_CONTEXT, label, payload);
  };

  // State to store suppliers with full data for editing
  const [suppliersWithFullData, setSuppliersWithFullData] = useState({});
  const [loadingSuppliers, setLoadingSuppliers] = useState({});

  // Function to fetch full supplier data for editing
  const fetchFullSupplierData = async supplierId => {
    logSupplierTableDebug('fetchFullSupplierData:start', {
      supplierId,
      isAlreadyLoading: !!loadingSuppliers[supplierId],
      hasCachedFullData: !!suppliersWithFullData[supplierId],
    });
    // Don't fetch if already loading or already fetched
    if (loadingSuppliers[supplierId] || suppliersWithFullData[supplierId]) {
      return;
    }

    setLoadingSuppliers(prev => ({ ...prev, [supplierId]: true }));

    try {
      const fullSupplierData = await getSupplierDetailsLazy(token, supplierId);

      setSuppliersWithFullData(prev => ({
        ...prev,
        [supplierId]: fullSupplierData,
      }));
    } catch (error) {
      logError('SupplierTable', 'fetchFullSupplierData failed', error);
      logSupplierTableDebug('fetchFullSupplierData:error', {
        supplierId,
        name: error.name,
        message: error.message,
      });
    } finally {
      setLoadingSuppliers(prev => ({ ...prev, [supplierId]: false }));
      logSupplierTableDebug('fetchFullSupplierData:finish', {
        supplierId,
        isLoading: false,
      });
    }
  };

  // Function to get the best available supplier data (full data if available, otherwise list data)
  const getSupplierDataForEditing = supplier => {
    // If we have cached full data, use it
    if (suppliersWithFullData[supplier.id]) {
      logSupplierTableDebug('getSupplierDataForEditing:using-cached', {
        supplierId: supplier.id,
      });
      return suppliersWithFullData[supplier.id];
    }
    
    // If supplier from list doesn't have secondary_emails, fetch full data
    // (list endpoint doesn't include secondary_emails for performance)
    if (supplier && !supplier.secondary_emails && !loadingSuppliers[supplier.id]) {
      logSupplierTableDebug('getSupplierDataForEditing:lazy-fetch-triggered', {
        supplierId: supplier.id,
      });
      fetchFullSupplierData(supplier.id);
    }
    
    return suppliersWithFullData[supplier.id] || supplier;
  };

  // Function to update the cache with full supplier data after update
  const updateSupplierCache = (supplierId, fullSupplierData) => {
    if (fullSupplierData) {
      // Update cache with the new full data
      setSuppliersWithFullData(prev => ({
        ...prev,
        [supplierId]: fullSupplierData,
      }));
      logSupplierTableDebug('updateSupplierCache:update', {
        supplierId,
        hasFullData: true,
      });
    } else {
      // If no data provided, invalidate the cache
      setSuppliersWithFullData(prev => {
        const updated = { ...prev };
        delete updated[supplierId];
        return updated;
      });
      setLoadingSuppliers(prev => {
        const updated = { ...prev };
        delete updated[supplierId];
        return updated;
      });
      logSupplierTableDebug('updateSupplierCache:invalidate', {
        supplierId,
      });
    }
  };

  return (
    <Table striped bordered hover>
      <thead>
        <tr className="text-center">
          <th>#</th>
          <SortableHeaderCell
            label="Name"
            sortKey="name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>Inner ID</th>
          <th>Website</th>
          <th>Main Email</th>
          <th>Office Phone</th>
          {!isViewer ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {supplierList.map((supplier, index) => {
          logSupplierTableDebug('render-row', {
            index,
            supplierId: supplier.id,
          });
          return (
          <tr key={supplier.id} className="text-center align-middle">
            <td>{index + 1}</td>
            <td>
              <SupplierDetailModal
                supplierObj={supplier}
                supplierId={supplier.id}
                updateSuppliers={() => {
                  logSupplierTableDebug(
                    'SupplierDetailModal:updateSuppliers',
                    {
                      supplierId: supplier.id,
                    }
                  );
                  handleChange();
                }}
                clearSearchValue={clearSearchValue}
              />
            </td>
            <td>{supplier.inner_supplier_id || '-'}</td>
            <td>
              <a
                href={supplier.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                <NavigateToUrlIcon />
              </a>
            </td>
            <td>{supplier.email}</td>
            <td>
              {supplier?.phone_prefix && supplier?.phone_suffix && (
                <span>
                  {supplier.phone_prefix}-{supplier.phone_suffix}
                </span>
              )}
            </td>
            {!isViewer ? (
            <td className="d-flex flex-row align-items-center justify-content-evenly">
              <SupplierModal
                supplierObj={getSupplierDataForEditing(supplier)}
                clearSearchValue={clearSearchValue}
                onEditButtonHover={() => {
                  logSupplierTableDebug('SupplierModal:onEditButtonHover', {
                    supplierId: supplier.id,
                  });
                  fetchFullSupplierData(supplier.id);
                }}
                isEditButtonLoading={loadingSuppliers[supplier.id]}
                onSupplierUpdate={updateSupplierCache}
              />
              <DeleteButton
                objectType="supplier"
                objectName={supplier.name}
                objectId={supplier.id}
                deleteFetchFunc={deleteSupplier}
                onSuccessfulDelete={() => {
                  logSupplierTableDebug('DeleteButton:onSuccessfulDelete', {
                    supplierId: supplier.id,
                  });
                  handleChange();
                }}
                clearSearchValue={clearSearchValue}
              />
            </td>
            ) : null}
          </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default SupplierTable;
